import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { IconSearch } from "@tabler/icons-react";
import { valueBand } from "./welcomeSurveyBands";
import "./WelcomeSurveyRepQuality.css";

// A rep at or above this blended score is performing; below it the score reads
// as behind. The design calls this out as an admin-configurable threshold
// rather than a constant, so it comes in as a prop and this is only the
// fallback.
export const DEFAULT_REP_TARGET = 80;

// The report credits sales to an employee it has no name for when neither
// FieldRoutes nor the rep roster carries one. The row still has to say whose
// column of figures it is.
const UNNAMED_REP = "Unnamed rep";

// Stands in for a figure the response didn't carry for a rep, so the cell
// reads as unreported rather than as a clean zero.
const METRIC_UNREPORTED = "The report didn't return this figure for this rep.";

function formatWindow(dateRange) {
  const [start, end] = dateRange ?? [];
  if (!start || !end) return "";
  return `${dayjs(start).format("MMM D")} – ${dayjs(end).format("MMM D")}`;
}

// The one place a figure from the response becomes a number. Null and
// undefined stay null rather than becoming Number(null)'s zero — a metric the
// report didn't send must not render as a rep scoring nothing.
function toNumber(value) {
  if (value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

// The report's percentages carry two decimals; this scoreboard shows whole
// numbers, the way its columns always have.
function formatPercent(value) {
  const number = toNumber(value);
  return number === null ? null : `${Math.round(number)}%`;
}

/// The report's `rep_quality_summary`, ready to render.
///
/// Every figure here is the endpoint's, not this client's. The report already
/// rolls its subscription rows up per selling rep, against the same
/// definitions its company-wide totals use:
///   - survey_completed_percentage — of the subscriptions the rep sold, the
///     share whose welcome survey the customer completed. Shown as "Survey".
///   - ror_percentage — the share that came straight back: cancelled inside
///     three days of the initial service, or before it ever happened. Shown as
///     "ROR".
///   - retention_percentage — the share still on the books, active or pending.
///     An account awaiting its first service has not been lost. Shown as
///     "Active".
///   - quality — the three as one score, ROR inverted because a low one is the
///     good outcome: round((survey + (100 - ror) + retention) / 3).
/// Re-deriving any of them here is how this panel and the totals above it
/// drift apart, so nothing below recomputes — it formats.
///
/// The response arrives ranked best-first, so the order is left alone.
function toRepRows(reps) {
  return (reps ?? []).map((rep, index) => ({
    // Names repeat about as often as people do and the summary carries no id,
    // so the response's own position keeps the key unique either way.
    key: `${index}-${rep?.name ?? ""}`,
    name: rep?.name || UNNAMED_REP,
    surveyPercent: formatPercent(rep?.survey_completed_percentage),
    rorPercent: formatPercent(rep?.ror_percentage),
    retentionPercent: formatPercent(rep?.retention_percentage),
    quality: toNumber(rep?.quality),
  }));
}

// One of the three metric columns: the figure, or a dash when the report had
// none to give.
function MetricCell({ value }) {
  return (
    <td className="ws-rq-metric">
      {value ?? (
        <span className="ws-rq-unknown" title={METRIC_UNREPORTED}>
          —
        </span>
      )}
    </td>
  );
}

/**
 * Per-rep scoreboard for the reporting window, straight from the report's own
 * `rep_quality_summary`. Sits beside the customer table as the "who" behind
 * the totals.
 */
function WelcomeSurveyRepQuality({
  reps: repSummary,
  dateRange,
  loading = false,
  repTarget = DEFAULT_REP_TARGET,
}) {
  const [search, setSearch] = useState("");

  const reps = useMemo(() => toRepRows(repSummary), [repSummary]);

  const query = search.trim().toLowerCase();
  const visibleReps = useMemo(() => {
    if (!query) return reps;
    return reps.filter(rep => rep.name.toLowerCase().includes(query));
  }, [reps, query]);

  const windowLabel = formatWindow(dateRange);

  return (
    <section className="ws-rq">
      <div className="ws-rq-header">
        <h2 className="ws-summary-heading">Rep Quality</h2>
        {windowLabel && (
          <div className="ws-summary-label ws-rq-window">{windowLabel}</div>
        )}
      </div>

      <div className="ws-rq-controls">
        <label className="ws-rq-search">
          <IconSearch size={15} />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search reps"
            aria-label="Search reps"
          />
        </label>
      </div>

      <div className="ws-rq-table-scroll">
        <table className="ws-rq-table">
          <thead>
            <tr>
              <th>Rep</th>
              <th className="ws-rq-metric">Survey</th>
              <th className="ws-rq-metric">ROR</th>
              <th className="ws-rq-metric">Active</th>
              <th className="ws-rq-metric ws-rq-score-col">Quality</th>
            </tr>
          </thead>
          <tbody>
            {visibleReps.map(rep => (
              <tr key={rep.key}>
                <td>
                  <div className="ws-rq-name" title={rep.name}>
                    {rep.name}
                  </div>
                  {/* The bar under the name is the blended score, so the
                      column of bars reads as the scoreboard at a glance. */}
                  {rep.quality !== null && (
                    <div
                      className="ws-rq-bar"
                      role="img"
                      aria-label={`Quality score ${rep.quality} of 100, target ${repTarget}`}
                    >
                      <span
                        className={`ws-rq-bar-fill ws-rq-bar-${valueBand(rep.quality)}`}
                        style={{ width: `${Math.min(rep.quality, 100)}%` }}
                      />
                    </div>
                  )}
                </td>
                <MetricCell value={rep.surveyPercent} />
                <MetricCell value={rep.rorPercent} />
                <MetricCell value={rep.retentionPercent} />
                <td className="ws-rq-metric ws-rq-score-col">
                  {rep.quality === null ? (
                    <span className="ws-rq-unknown" title={METRIC_UNREPORTED}>
                      —
                    </span>
                  ) : (
                    <span
                      className={`ws-rq-score ws-rq-score-${valueBand(rep.quality)}`}
                      title={
                        rep.quality < repTarget
                          ? `Below the ${repTarget} target`
                          : undefined
                      }
                    >
                      {rep.quality}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {visibleReps.length > 0 && (
          <div className="ws-rq-end">
            <p className="ws-rq-end-line">
              {query
                ? `Showing ${visibleReps.length} of ${reps.length} reps.`
                : `That's every rep with a sale${windowLabel ? ` in ${windowLabel}` : ""}.`}
            </p>
            {!query && (
              <p className="ws-rq-end-note">
                A rep joins the list once a sale in this window is credited to
                them.
              </p>
            )}
          </div>
        )}

        {visibleReps.length === 0 && (
          <div className="ws-rq-empty">
            {loading
              ? "Loading reps…"
              : query
                ? `No reps match “${search.trim()}”.`
                : "No reps sold in this window."}
          </div>
        )}
      </div>
    </section>
  );
}

export default WelcomeSurveyRepQuality;
